import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiZap, FiCalendar, FiTrendingUp, FiShield, FiUsers, FiFileText, FiBarChart2, FiCreditCard, FiLink, FiShare2, FiCopy, FiX, FiEdit2, FiDownload } from 'react-icons/fi';
import { FaStar, FaTrophy } from 'react-icons/fa';
import './TraderProfile.css';
import { TrophyShowcase, Avatar, PixelIcon } from '../Achievements/Achievements';
import Identicon from '../common/Identicon';
import { levelInfo as computeLevel, levelColor, frameForLevel, trophyBreakdown, featuredTrophies, rarityOf } from '../../lib/achievements';
import { useAuth } from '../../Context/AuthContext';
import TraderActions from './TraderActions';
import EditProfile from '../EditProfile/EditProfile';

import { apiFetch } from '../../services/apiClient';

// Presets de banner para el storefront (id guardado en bannerColor)
const BANNER_PRESETS = [
  { id: 'verde',   css: 'linear-gradient(135deg,#00d412,#007850)' },
  { id: 'cian',    css: 'linear-gradient(135deg,#22d3ee,#2775ca)' },
  { id: 'magenta', css: 'linear-gradient(135deg,#d946ef,#7b3fe4)' },
  { id: 'oro',     css: 'linear-gradient(135deg,#f0b90b,#f4511e)' },
  { id: 'noche',   css: 'linear-gradient(135deg,#243b27,#0a0e0b)' },
];
function bannerCss(val) {
  if (!val) return null;
  const p = BANNER_PRESETS.find((b) => b.id === val);
  if (p) return p.css;
  if (/^(#|linear|rgb)/.test(val)) return val;   // permite un color/gradiente custom
  return null;
}

// Horario de atención → estado "abierto ahora"
const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
function storeStatus(t) {
  if (t.openFrom == null || t.openTo == null) return null;
  const now = new Date(), day = now.getDay(), hour = now.getHours();
  const days = String(t.openDays || '1,2,3,4,5').split(',').map(Number).filter((n) => !Number.isNaN(n));
  const inDay = days.includes(day);
  const inHour = t.openFrom <= t.openTo ? (hour >= t.openFrom && hour < t.openTo) : (hour >= t.openFrom || hour < t.openTo);
  return { open: inDay && inHour, from: t.openFrom, to: t.openTo, days };
}
function daysLabel(days) {
  if (!days || !days.length) return '';
  const set = new Set(days);
  if (days.length === 7) return 'Todos los días';
  if ([1, 2, 3, 4, 5].every((d) => set.has(d)) && !set.has(0) && !set.has(6)) return 'L–V';
  return days.slice().sort((a, b) => a - b).map((d) => DOW[d]).join(' ');
}

function StarRating({ value, max = 5 }) {
  return (
    <div className="tp-star-rating">
      {Array.from({ length: max }, (_, i) => (
        <FaStar key={i} className={`tp-star-glyph ${i < value ? 'on' : 'off'}`} />
      ))}
    </div>
  );
}

// Modal "Compartir perfil de comerciante" — link + QR + tarjeta descargable + redes
function ProfileShareModal({ trader, frame, lvlColor, level, rank, trades, rating, trophies, onClose }) {
  const [copied, setCopied] = useState(false);
  // Link vanity si hay slug, si no el clásico por usuario
  const url   = trader.slug ? `https://app.bitplify.io/@${trader.slug}` : `https://app.bitplify.io/profile/${trader.username}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=170x170&margin=4&data=${encodeURIComponent(url)}`;
  const msg   = `🚀 Mirá mi perfil de comerciante en Bitplify P2P\n\n👤 @${trader.username} · ⬡ ${rank} · Nivel ${level}\n🤝 ${trades} trades · ⭐ ${rating} · 🏆 ${trophies} logros\n\n👉 ${url}\n💡 El mercado sos vos`;
  const copy  = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const [copiedRef, setCopiedRef] = useState(false);
  const refUrl   = `https://app.bitplify.io/register?ref=${trader.slug || trader.username}`;
  const copyRef  = () => { navigator.clipboard.writeText(refUrl); setCopiedRef(true); setTimeout(() => setCopiedRef(false), 2000); };

  // Tarjeta de comerciante → PNG (dibujada a mano en canvas, sin dependencias)
  const downloadCard = () => {
    const W = 640, H = 340, s = 2;
    const c = document.createElement('canvas');
    c.width = W * s; c.height = H * s;
    const x = c.getContext('2d');
    x.scale(s, s);
    x.fillStyle = '#0a0e0b'; x.fillRect(0, 0, W, H);
    const g = x.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0, lvlColor + '55'); g.addColorStop(1, 'rgba(10,14,11,0)');
    x.fillStyle = g; x.fillRect(0, 0, W, H);
    x.fillStyle = lvlColor; x.fillRect(0, 0, W, 6);
    // avatar (inicial sobre disco del color de nivel)
    const cx = 72, cy = 88, r = 38;
    x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.fillStyle = lvlColor; x.fill();
    x.fillStyle = '#04130d'; x.font = '700 30px Inter, system-ui, sans-serif';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText((trader.name || trader.username || '?').slice(0, 2).toUpperCase(), cx, cy + 1);
    x.textAlign = 'left'; x.textBaseline = 'alphabetic';
    x.fillStyle = '#ffffff'; x.font = '800 32px Inter, system-ui, sans-serif';
    x.fillText((trader.name || trader.username || '').slice(0, 24), 128, 80);
    x.fillStyle = '#a7c0ae'; x.font = '600 17px Inter, system-ui, sans-serif';
    x.fillText(`@${trader.username}  ·  ${rank}`, 128, 106);
    x.fillStyle = '#e8f3ea'; x.font = '700 19px Inter, system-ui, sans-serif';
    x.fillText(`★ ${rating}    ·    ${trades} trades    ·    ${trophies} logros    ·    Nivel ${level}`, 40, 172);
    if (trader.headline) { x.fillStyle = '#cfe8d4'; x.font = '500 18px Inter, system-ui, sans-serif'; x.fillText(String(trader.headline).slice(0, 48), 40, 210); }
    x.fillStyle = lvlColor; x.font = '800 20px Inter, system-ui, sans-serif'; x.fillText('₿ Bitplify P2P', 40, H - 34);
    x.fillStyle = '#7e9686'; x.font = '600 15px Inter, system-ui, sans-serif'; x.fillText(url.replace('https://', ''), 40, H - 14);
    c.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `bitplify-${trader.username}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, 'image/png');
  };

  return (
    <div className="tp-share-overlay" onClick={onClose}>
      <div className="tp-share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tp-share-head">
          <h3><FiShare2 className="tp-share-head-ic" /> Compartir perfil</h3>
          <button className="tp-share-close" onClick={onClose} aria-label="Cerrar"><FiX /></button>
        </div>
        <div className="tp-share-body">
          <div className="tp-share-card" style={{ '--accent': lvlColor }}>
            <div className="tp-share-card-bar" />
            <Avatar u={{ username: trader.username, name: trader.name, avatar: trader.avatar }} frame={frame} size={60} />
            <div className="tp-share-card-id">
              <div className="tp-share-card-name">{trader.name} {trader.verified && <FiCheck className="tp-share-verif" />}</div>
              <div className="tp-share-card-user">@{trader.username} · ⬡ {rank}</div>
              <div className="tp-share-card-stats">
                <span><FaStar className="tp-share-star" /> <strong className="fin-num">{rating}</strong></span>
                <span><strong className="fin-num">{trades}</strong> trades</span>
                <span><FaTrophy className="tp-share-trophy" /> <strong className="fin-num">{trophies}</strong></span>
                <span>Nv.<strong className="fin-num">{level}</strong></span>
              </div>
            </div>
            <div className="tp-share-card-brand">₿ Bitplify P2P</div>
          </div>

          <div className="tp-share-qr-row">
            <img className="tp-share-qr" src={qrSrc} alt="QR de tu perfil" width={84} height={84} />
            <div className="tp-share-qr-side">
              <div className="tp-share-url">
                <span>{url}</span>
                <button className={copied ? 'copied' : ''} onClick={copy}>{copied ? <><FiCheck /> Copiado</> : <><FiCopy /> Copiar</>}</button>
              </div>
              <button className="tp-share-download" onClick={downloadCard}><FiDownload /> Descargar tarjeta (PNG)</button>
            </div>
          </div>

          <div className="tp-share-social">
            <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" className="tp-share-btn wa">WhatsApp</a>
            <a href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" className="tp-share-btn tg">Telegram</a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(msg.slice(0, 240))}`} target="_blank" rel="noopener noreferrer" className="tp-share-btn tw">Twitter/X</a>
          </div>
          <div className="tp-share-ref">
            <span className="tp-share-ref-label">🎁 Invitá y ganá <small>— quien se registre por tu link queda asociado a vos</small></span>
            <button className={`tp-share-ref-btn ${copiedRef ? 'copied' : ''}`} onClick={copyRef}>{copiedRef ? <><FiCheck /> Copiado</> : <><FiCopy /> Copiar link de invitación</>}</button>
          </div>
          <p className="tp-share-hint">Tu reputación te precede — compartí tu QR o tarjeta y operá con confianza.</p>
        </div>
      </div>
    </div>
  );
}

// El editor del storefront ahora vive en el componente compartido EditProfile
// (cuenta + tienda en un solo lugar), reusado por el perfil privado y este público.

export default function TraderProfile({ offers = [] }) {
  const { trader: rawTrader } = useParams();
  const traderParam = rawTrader ? rawTrader.replace(/^@/, '') : rawTrader;   // soporta link vanity /@usuario
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');
  const [reviewSort, setReviewSort] = useState('recent');
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/users/${traderParam}`);
        if (res.status === 404) throw new Error('Trader no encontrado');
        if (!res.ok) {
          let m = 'No se pudo cargar el perfil (error del servidor).';
          try { m = (await res.json()).message || m; } catch {}
          throw new Error(m);
        }
        setProfile(await res.json());
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    if (traderParam) fetchProfile();
  }, [traderParam]);

  if (loading) return (
    <div className="profile-wrapper">
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--mk-text-tertiary)' }}>
        <span className="tp-spinner" /> Cargando perfil...
      </div>
    </div>
  );

  if (error) return (
    <div className="profile-wrapper">
      <div style={{ textAlign: 'center', padding: '60px', color: '#f4511e' }}>
        <FiUsers style={{ width: 32, height: 32, opacity: 0.5, display: 'block', margin: '0 auto 12px' }} />
        <p style={{ fontWeight: 700 }}>{error}</p>
        <p style={{ color: 'var(--mk-text-tertiary)', fontSize: '0.82rem', marginTop: 6 }}>Usuario buscado: <code>{traderParam}</code></p>
        <button onClick={() => navigate('/')} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f0f2f5', cursor: 'pointer' }}>
          <FiArrowLeft style={{ marginRight: 4, verticalAlign: -2 }} /> Volver al Marketplace
        </button>
      </div>
    </div>
  );

  const trader = profile;
  const isOwner = !!user && (
    (user.username && trader.username && String(user.username).toLowerCase() === String(trader.username).toLowerCase()) ||
    (user.id != null && trader.id != null && user.id === trader.id)
  );
  const bannerBg = bannerCss(trader.bannerColor);
  const status = storeStatus(trader);
  const pinnedSet = new Set(trader.pinnedOffers || []);
  const togglePin = async (offerId) => {
    const cur = new Set(profile.pinnedOffers || []);
    if (cur.has(offerId)) cur.delete(offerId);
    else { if (cur.size >= 3) return; cur.add(offerId); }
    const next = [...cur];
    setProfile((p) => ({ ...p, pinnedOffers: next }));
    try { await apiFetch('/api/me', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pinnedOffers: next }) }); } catch {}
  };
  const traderStats = {
    trades: trader.trades, streak: trader.streak, level: trader.level, xp: trader.xp,
    averageRating: trader.averageRating ?? trader.avgRating, totalReviews: trader.totalReviews ?? trader.reviewCount,
    verified: trader.verified, kycLevel: trader.kycLevel,
  };
  const lvl = computeLevel(trader.xp ?? 0);
  const lvlColor = levelColor(lvl.level);
  const frame = frameForLevel(trader.level ?? lvl.level);
  const totalTrades = trader.trades ?? 0;
  const repPct = trader.reviewCount > 0 ? Math.round((trader.positiveReviews / trader.reviewCount) * 100) : 100;

  // Reputación de trading estilo P2P (usa datos reales si llegan; si no, deriva estable)
  const _rh = (() => { const s = String(trader.username || trader.name || ''); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; })();
  const _rating = trader.avgRating ?? trader.averageRating ?? 4.6;
  const completionRate = trader.completionRate ?? Math.min(99.9, +(90 + (_rating / 5) * 7 + Math.min(totalTrades / 500, 1) * 2 + (_rh % 10) / 10).toFixed(1));
  const avgRelease = trader.avgReleaseMin ?? Math.max(1, 2 + (_rh % 6));
  const orders30d = trader.orders30d ?? Math.max(3, Math.round(totalTrades * 0.06) + (_rh % 12));
  const repCards = [
    { label: 'Finalización', value: `${completionRate}%`, accent: completionRate >= 95 },
    { label: 'Tiempo de liberación', value: `~${avgRelease} min` },
    { label: 'Órdenes (30d)', value: orders30d.toLocaleString('es') },
    { label: 'Valoraciones positivas', value: `${repPct}%`, accent: repPct >= 95 },
  ];

  const breakdown = trophyBreakdown(traderStats);
  const featured = featuredTrophies(traderStats, 6);
  const trophiesUnlocked = breakdown.reduce((s, b) => s + b.unlocked, 0);
  const trophiesTotal = breakdown.reduce((s, b) => s + b.total, 0);

  const stats = [
    { Icon: FiCheck,       label: 'Trades completados', value: totalTrades },
    { Icon: FiZap,         label: 'XP Total',           value: (trader.xp ?? 0).toLocaleString() },
    { Icon: FiTrendingUp,  label: 'Racha actual',       value: `${trader.streak ?? 0} días` },
    { Icon: FaTrophy,      label: 'Logros',             value: `${trophiesUnlocked}/${trophiesTotal}` },
    { Icon: FiCalendar,    label: 'Miembro desde',      value: new Date(trader.createdAt).toLocaleDateString('es', { month: 'short', year: 'numeric' }) },
    { Icon: FiUsers,       label: 'Referidos',          value: trader.referralCount ?? 0 },
    { Icon: FiShield,      label: 'Rango',              value: trader.rank ?? lvl.rank },
  ];

  const traderOffers = trader.offers ?? [];
  const acceptedPays = [...new Set(traderOffers.map((o) => o.payment).filter(Boolean))].slice(0, 8);
  const acceptedNets = [...new Set(traderOffers.map((o) => o.network).filter(Boolean))].slice(0, 6);
  const sortedOffers = [...traderOffers].sort((a, b) => (pinnedSet.has(b.id) ? 1 : 0) - (pinnedSet.has(a.id) ? 1 : 0));

  return (
    <div className="profile-wrapper">
      <div className="profile-page">
        <div className="main-content">

          <div className="tp-top-actions">
            <button className="tp-back-btn" onClick={() => navigate(-1)}>
              <FiArrowLeft className="tp-back-icon" /> Volver
            </button>
            <div className="tp-top-right">
              {isOwner && (
                <button className="tp-edit-trigger" onClick={() => setEditOpen(true)}>
                  <FiEdit2 className="tp-share-trigger-ic" /> Editar perfil
                </button>
              )}
              <button className="tp-share-trigger" onClick={() => setShareOpen(true)}>
                <FiShare2 className="tp-share-trigger-ic" /> {isOwner ? 'Compartir mi perfil' : 'Compartir perfil'}
              </button>
            </div>
          </div>

          {/* HERO — player card estilo consola */}
          <section className="trader-hero tp-hero-psn" style={{ '--accent': lvlColor }}>
            <div className={`tp-banner ${bannerBg ? 'tp-banner-custom' : ''}`} style={bannerBg ? { background: bannerBg } : undefined} />
            <div className="tp-hero-body">
              <div className="hero-left">
                <Avatar u={{ username: trader.username, name: trader.name, avatar: trader.avatar }} frame={frame} size={104} />
                <div className="hero-info">
                  <div className="hero-name-row">
                    <h2 className="hero-name">{trader.name}</h2>
                    {trader.verified && <span className="tp-verified-badge"><FiCheck /> Verificado</span>}
                    {trader.walletAddress && <span className="tp-verified-badge tp-selfcustody-badge"><FiShield /> Autocustodia</span>}
                  </div>
                  <p className="tp-username">@{trader.username}</p>
                  <div className="tp-rank-row">
                    <span className="tp-rank-chip" style={{ color: lvlColor, borderColor: `${lvlColor}55` }}>⬡ {trader.rank ?? lvl.rank}</span>
                    {status
                      ? <span className={`tp-open-chip ${status.open ? 'on' : 'off'}`}><span className="tp-open-dot" />{status.open ? 'Abierto ahora' : 'Cerrado'}</span>
                      : <span className="status-row"><span className="status-dot online" /> <span className="status-text">Activo</span></span>}
                    <span className="tp-since"><FiCalendar className="tp-since-ic" /> Desde {new Date(trader.createdAt).toLocaleDateString('es', { month: 'short', year: 'numeric' })}</span>
                  </div>
                  {trader.headline && <p className="tp-headline">{trader.headline}</p>}
                  <TraderActions traderId={trader.id} traderName={trader.username} />
                </div>
              </div>

              <div className="tp-level-medallion" style={{ '--pct': lvl.pct, '--ring': lvlColor }}>
                <div className="tp-medallion-inner">
                  <span className="tp-medallion-lv">{trader.level ?? lvl.level}</span>
                  <span className="tp-medallion-lbl">NIVEL</span>
                </div>
              </div>
            </div>

            <div className="tp-hero-xp">
              <div className="tp-xp-track"><div className="tp-xp-fill" style={{ width: `${lvl.pct}%`, background: lvlColor }} /></div>
              <span className="tp-xp-text">
                {(trader.xp ?? 0).toLocaleString('es')} XP
                {!lvl.isMax ? ` · ${lvl.toNext.toLocaleString('es')} para Nv.${lvl.level + 1}` : ' · nivel máximo'}
              </span>
            </div>
          </section>

          {trader.bio && (
            <section className="tp-bio-section">
              <p className="tp-bio">{trader.bio}</p>
            </section>
          )}

          {(acceptedPays.length > 0 || acceptedNets.length > 0 || status) && (
            <section className="tp-accept">
              {status && (
                <div className="tp-accept-group">
                  <span className="tp-accept-label"><FiCalendar className="tp-accept-ic" /> Horario</span>
                  <span className="tp-hours-text">{daysLabel(status.days)} · {status.from}:00–{status.to}:00 h</span>
                </div>
              )}
              {acceptedPays.length > 0 && (
                <div className="tp-accept-group">
                  <span className="tp-accept-label"><FiCreditCard className="tp-accept-ic" /> Acepta</span>
                  <div className="tp-accept-chips">{acceptedPays.map((p) => <span key={p} className="tp-accept-chip">{p}</span>)}</div>
                </div>
              )}
              {acceptedNets.length > 0 && (
                <div className="tp-accept-group">
                  <span className="tp-accept-label"><FiLink className="tp-accept-ic" /> Redes</span>
                  <div className="tp-accept-chips">{acceptedNets.map((n) => <span key={n} className="tp-accept-chip">{n}</span>)}</div>
                </div>
              )}
            </section>
          )}

          {/* TROPHY CABINET — estilo PSN */}
          <section className="tp-cabinet">
            <div className="tp-cabinet-head">
              <h3><FaTrophy className="tp-section-icon" /> Vitrina de logros</h3>
              <span className="tp-cabinet-total"><strong className="fin-num">{trophiesUnlocked}</strong>/{trophiesTotal} · {Math.round((trophiesUnlocked / trophiesTotal) * 100)}%</span>
            </div>

            <div className="tp-tier-row">
              {breakdown.map((b) => (
                <div key={b.key} className={`tp-tier-chip ${b.unlocked === 0 ? 'empty' : ''}`} style={{ '--tc': b.color }}>
                  <span className="tp-tier-dot" />
                  <span className="tp-tier-count fin-num">{b.unlocked}</span>
                  <span className="tp-tier-name">{b.label}</span>
                </div>
              ))}
            </div>

            {featured.length > 0 ? (
              <div className="tp-featured">
                <div className="tp-featured-label">Destacados</div>
                <div className="tp-featured-row">
                  {featured.map((t) => (
                    <div key={t.id} className="tp-featured-item" title={t.label}>
                      <PixelIcon sprite={t.pix} color={t.color} size={44} />
                      <span className="tp-featured-name">{t.label}</span>
                      <span className="tp-featured-rarity" style={{ color: rarityOf(t).color }}>{rarityOf(t).rarity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="tp-featured-empty">Este trader aún no desbloqueó logros. ¡Pronto empieza a coleccionar!</div>
            )}
          </section>

          {/* TABS */}
          <div className="tp-tabs">
            {[
              { key: 'stats',    label: 'Estadísticas', Icon: FiBarChart2 },
              { key: 'offers',   label: 'Ofertas',      Icon: FiFileText },
              { key: 'reviews',  label: 'Reviews',      Icon: FaStar },
              { key: 'trophies', label: 'Trofeos',      Icon: FaTrophy },
            ].map(t => (
              <button key={t.key} className={`tp-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
                <t.Icon className="tp-tab-icon" /> {t.label}
              </button>
            ))}
          </div>

          {/* STATS */}
          {activeTab === 'stats' && (
            <section className="stats-section">
              <div className="tp-rep-head"><FiShield className="tp-section-icon" /> Reputación de trading</div>
              <div className="tp-rep-grid">
                {repCards.map((r, i) => (
                  <div className="tp-rep-card" key={i}>
                    <span className={`tp-rep-val fin-num ${r.accent ? 'up' : ''}`}>{r.value}</span>
                    <span className="tp-rep-lbl">{r.label}</span>
                  </div>
                ))}
              </div>
              <div className="stats-grid">
                {stats.map((s, i) => (
                  <div className="stat-card" key={i}>
                    <s.Icon className="stat-icon-svg" />
                    <span className="stat-label fin-label">{s.label}</span>
                    <span className="stat-value fin-num">{s.value}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* OFFERS */}
          {activeTab === 'offers' && (
            <section className="stats-section">
              {traderOffers.length === 0 ? (
                <div className="tp-empty">
                  <FiFileText className="tp-empty-icon" />
                  <p>Este trader no tiene ofertas activas en este momento.</p>
                </div>
              ) : (
                <div className="tp-offers-list">
                  {sortedOffers.map((o, i) => {
                    const isPinned = o.id != null && pinnedSet.has(o.id);
                    return (
                      <div key={o.id ?? i} className={`tp-offer-card ${isPinned ? 'pinned' : ''}`}>
                        <div className="tp-offer-left">
                          {isPinned && <span className="tp-offer-pin-badge">★ Destacada</span>}
                          <span className={`mk-type-badge fin-chip ${o.type}`}>{o.type === 'buy' ? 'Compra' : 'Venta'}</span>
                          <strong>USDT <span className="fin-num">{Number(o.amount).toLocaleString()}</span></strong>
                        </div>
                        <div className="tp-offer-right">
                          <span className="fin-chip"><FiLink className="tp-offer-ic" /> {o.network}</span>
                          <span className="fin-chip"><FiCreditCard className="tp-offer-ic" /> {o.payment}</span>
                          <span className="fin-num tp-offer-spread" style={{ color: o.spread < 0 ? 'var(--fin-up)' : o.spread > 0 ? 'var(--fin-down)' : 'var(--mk-text-tertiary)' }}>
                            {o.spread === 0 ? 'TRM' : o.spread > 0 ? `+${o.spread}%` : `${o.spread}%`}
                          </span>
                          {isOwner && o.id != null && (
                            <button className={`tp-offer-pin ${isPinned ? 'on' : ''}`} onClick={() => togglePin(o.id)} title={isPinned ? 'Quitar destacada' : 'Destacar (máx 3)'}>★</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* REVIEWS */}
          {activeTab === 'reviews' && (
            <section className="stats-section">
              {(!trader.reviews || trader.reviews.length === 0) ? (
                <div className="tp-empty">
                  <FaStar className="tp-empty-icon" />
                  <p>Este trader aún no tiene reviews.</p>
                </div>
              ) : (() => {
                const bd = trader.ratingBreakdown || {};
                const bdTotal = [5, 4, 3, 2, 1].reduce((s, k) => s + (bd[k] || 0), 0) || trader.reviewCount || 1;
                const sorted = [...trader.reviews].sort((a, b) =>
                  reviewSort === 'best' ? b.rating - a.rating :
                  reviewSort === 'worst' ? a.rating - b.rating : 0
                );
                const CAT_LABELS = { communication: 'Comunicación', speed: 'Velocidad', accuracy: 'Precisión' };
                return (
                  <div className="tp-reviews-list">
                    <div className="tp-review-summary">
                      <div className="tp-review-avg">
                        <span className="tp-avg-number fin-num">{trader.avgRating?.toFixed(1) ?? '—'}</span>
                        <StarRating value={Math.round(trader.avgRating ?? 0)} />
                        <span className="tp-review-count-sub"><span className="fin-num">{trader.reviewCount}</span> reviews</span>
                      </div>
                      <div className="tp-review-dist">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const n = bd[star] || 0;
                          return (
                            <div key={star} className="tp-dist-row">
                              <span className="tp-dist-star">{star}<FaStar className="tp-dist-star-ic" /></span>
                              <div className="tp-dist-track"><div className="tp-dist-fill" style={{ width: `${(n / bdTotal) * 100}%` }} /></div>
                              <span className="tp-dist-count fin-num">{n}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="tp-review-filters">
                      {[
                        { key: 'recent', label: 'Recientes' },
                        { key: 'best',   label: 'Mejores' },
                        { key: 'worst',  label: 'Peores' },
                      ].map((f) => (
                        <button key={f.key} className={`tp-review-filter ${reviewSort === f.key ? 'active' : ''}`} onClick={() => setReviewSort(f.key)}>
                          {f.label}
                        </button>
                      ))}
                    </div>

                    {sorted.map((r, i) => {
                      const cats = Object.entries(r.categories || {}).filter(([, v]) => v != null);
                      return (
                        <div key={i} className="tp-review-card">
                          <div className="tp-review-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Identicon seed={r.author || r.name} src={r.avatar} size={36} className="tp-review-avatar" alt={r.author} />
                              <div>
                                <div className="tp-review-author">@{r.author}</div>
                                <div className="tp-review-date fin-num">{r.date}</div>
                              </div>
                            </div>
                            <StarRating value={r.rating} />
                          </div>
                          {r.comment && <p className="tp-review-comment">"{r.comment}"</p>}
                          {cats.length > 0 && (
                            <div className="tp-review-cats">
                              {cats.map(([k, v]) => (
                                <span key={k} className="tp-review-cat-chip">
                                  {CAT_LABELS[k] || k} <strong className="fin-num">{v}.0</strong><FaStar className="tp-cat-star" />
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>
          )}

          {/* TROPHIES */}
          {activeTab === 'trophies' && (
            <section className="badges-section tp-trophies">
              <TrophyShowcase u={traderStats} />
            </section>
          )}

        </div>
      </div>

      {shareOpen && (
        <ProfileShareModal
          trader={trader}
          frame={frame}
          lvlColor={lvlColor}
          level={trader.level ?? lvl.level}
          rank={trader.rank ?? lvl.rank}
          trades={totalTrades}
          rating={(trader.avgRating ?? trader.averageRating ?? _rating).toFixed ? (trader.avgRating ?? trader.averageRating ?? _rating).toFixed(1) : _rating}
          trophies={trophiesUnlocked}
          onClose={() => setShareOpen(false)}
        />
      )}

      {editOpen && (
        <EditProfile
          initial={trader}
          onClose={() => setEditOpen(false)}
          onSaved={(u) => { setProfile((p) => ({ ...p, ...u })); setEditOpen(false); }}
        />
      )}
    </div>
  );
}
